import { Timestamp } from "gen/google/protobuf/timestamp_pb"
import {
  BillPayment_Tax,
  Receivable,
  Receivable_Type,
  TaxLine,
} from "gen/txn/models_pb"

import { FieldMapping } from "../adapters"
import {
  BaseReceivable,
  BillTax,
  ReceivableKind,
  ReceivableKinds,
} from "./receivable.type"
import { Decimal } from "../classes/decimal"
import { Money } from "../classes/money"
import { User } from "../classes/user"
import { UUID } from "../classes/uuid"
import { ensure } from "../utils"

interface HasReceivable {
  receivable?: Receivable
}

export function getReceivableMapping(allowDiscount = false) {
  const agingReceivableMapping: FieldMapping<HasReceivable, BaseReceivable> = {
    id: (res) =>
      res.receivable?.type === Receivable_Type.INVOICE
        ? UUID.fromPBOrThrow(
            res.receivable?.transaction?.id,
            "Missing receivable.transaction.id"
          )
        : UUID.fromPBOrThrow(res.receivable?.id, "Missing receivable.id"),
    ledgerEntryId: (res) =>
      UUID.fromPBOrThrow(res.receivable?.id, "Missing receivable.id"),
    identifier: (res) =>
      ensure(
        res.receivable?.transaction?.identifier,
        "Missing receivable.transaction.identifier"
      ),
    kind: (res) => {
      const kindMapper: Record<Receivable_Type, ReceivableKind> = {
        [Receivable_Type.UNDEFINED]: ReceivableKinds.INVOICE,
        [Receivable_Type.INVOICE]: ReceivableKinds.INVOICE,
        [Receivable_Type.CREDIT]: ReceivableKinds.CREDIT,
        [Receivable_Type.FINANCE_CHARGE]: ReceivableKinds.FINANCE_CHARGE,
      }
      return kindMapper[ensure(res.receivable?.type, "Missing receivable.type")]
    },
    date: (res) =>
      Timestamp.toDate(ensure(res.receivable?.date, "Missing receivable date")),
    adjustedBalance: (res) => {
      if (!allowDiscount) {
        return Money.fromPB(res.receivable?.balance)
      }
      return Money.fromPB(res.receivable?.balance).add(
        Money.fromPB(res.receivable?.invoice?.potentialDiscount)
      )
    },
    balance: (res) => Money.fromPB(res.receivable?.balance),
    original: (res) => Money.fromPB(res.receivable?.original),
    dueDate: (res) =>
      res.receivable?.invoice?.dueDate &&
      Timestamp.toDate(res.receivable.invoice.dueDate),
    potentialDiscountExpiresAt: (res) =>
      res.receivable?.invoice?.potentialDiscountExpiresAt &&
      Timestamp.toDate(res.receivable.invoice.potentialDiscountExpiresAt),
    potentialDiscount: (res) =>
      Money.fromPB(res.receivable?.invoice?.potentialDiscount),
    purchaseOrder: (res) => res.receivable?.purchaseOrder?.name,
    location: (res) => ({
      prefix: ensure(
        res.receivable?.location?.prefix,
        "Missing location.prefix"
      ),
      name: ensure(res.receivable?.location?.name, "Missing location.name"),
    }),
    customer: (res) => ({
      id: UUID.fromPBOrThrow(
        res.receivable?.customer?.id,
        "Missing customer.id"
      ),
      identifier: ensure(
        res.receivable?.customer?.identifier,
        "Missing customer.identifier"
      ),
      name: ensure(res.receivable?.customer?.name, "Missing customer.name"),
    }),
    staffName: (res) =>
      res.receivable?.user && User.fromPB(res.receivable.user).displayName,
    job: (res) => {
      if (!res.receivable?.job?.id) return undefined
      return {
        id: UUID.fromPB(res.receivable.job.id),
        identifier: res.receivable.job.identifier,
        name: res.receivable.job.name,
      }
    },
    transaction: ({ receivable }) => ({
      id: UUID.fromPBOrThrow(
        receivable?.transaction?.id,
        "Missing receivable.transaction.id"
      ),
      identifier: ensure(
        receivable?.transaction?.identifier,
        "Missing receivable.transaction.identifier"
      ),
      type: ensure(
        receivable?.transaction?.type,
        "Missing receivable.transaction.type"
      ),
    }),
  }
  return agingReceivableMapping
}

// cardFeeTaxTotal sums the per-rate card-fee tax lines into a positive total,
// matching the positive surcharge and payment amounts BillTotals holds.
export function cardFeeTaxTotal(taxes: BillTax[]): Money {
  return taxes.reduce((total, tax) => total.add(tax.surchargeTax), Money.zero())
}

// Card-fee tax displays never show zero-tax rates. Both adapters filter on this
// at the boundary every BillTax[] flows through, so consumers don't re-filter.
function nonZeroTax(tax: BillTax): boolean {
  return tax.surchargeTax.isNonZero()
}

// taxesFromLines builds card-fee tax lines from the server's name-joined
// TaxLine list (bill detail, surcharge preview), which carry the rate name and
// a backend-built display label.
export function taxesFromLines(taxes: TaxLine[]): BillTax[] {
  return taxes
    .map((tax) => ({
      taxRateId: UUID.fromPBOrThrow(tax.taxRateId),
      name: tax.taxRateName,
      rate: Decimal.fromPBOrZero(tax.rate),
      surchargeTax: Money.fromPB(tax.tax),
      label: tax.label,
    }))
    .filter(nonZeroTax)
}

// taxesFromMap builds card-fee tax lines from the stored per-rate map for the
// bills-search path, which only sums surchargeTax — so it skips the display
// label that the name-joined taxesFromLines path builds.
export function taxesFromMap(
  taxesByTaxRateId: { [key: string]: BillPayment_Tax } | undefined
): BillTax[] {
  return Object.values(taxesByTaxRateId ?? {})
    .map((tax) => ({
      taxRateId: UUID.fromPBOrThrow(tax.id),
      name: "",
      rate: Decimal.fromPBOrZero(tax.rate),
      surchargeTax: Money.fromPB(tax.surchargeTax),
    }))
    .filter(nonZeroTax)
}
